import { useState, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import {
  barrattAPI,
  type ImportException,
  type SupplierUploadDatasetType,
  type SupplierUploadTracking,
} from '../../../lib/api/barratt';
import { useFetchData, getErrorMessage } from '../../../hooks/useFetchData';
import { DATASET_ORDER, TEMPLATE_ID_BY_DATASET, datasetLabel, periodKey, periodLabelFor } from '../uploadCycle';

/** A file the importer rejected, and why — shown in a modal, never a toast. */
export interface UploadFailure {
  summaryId: number;
  fileName: string;
  datasetType: SupplierUploadDatasetType;
  datasetLabel: string;
  periodLabel: string;
  problems: ImportException[];
  /**
   * True when the month already had a file. A rejected replace changes
   * nothing, so telling the supplier the month is "still outstanding" would
   * send them chasing a file that is already in.
   */
  hadFileAlready: boolean;
  /** Rows read from the file, and how many of them failed. */
  totalRows?: number;
  rejectedRows?: number;
  /** Problem counts for the whole file — the list itself is capped. */
  counts?: { error: number; warning: number };
  /** The request never reached the importer, so there is nothing to itemise. */
  transportError?: boolean;
}

/** One month of obligations for the logged-in supplier. */
export interface ObligationPeriod {
  key: string;
  label: string;
  dueDate: string | null;
  rows: SupplierUploadTracking[];
}

export interface UseMyObligationsResult {
  periods: ObligationPeriod[];
  /** Name of the customer the files are for, from the first row. */
  customerName: string | null;
  loading: boolean;
  error: string | null;
  retry: () => void;
  upload: (row: SupplierUploadTracking, file: File) => Promise<void>;
  /** Tracking row id currently uploading, or null. */
  uploadingId: number | null;
  /** The last rejected file, until dismissed. */
  failure: UploadFailure | null;
  dismissFailure: () => void;
}

/**
 * The reason a request failed, in the server's own words.
 *
 * A rejected file never reaches the importer, so there are no exception rows:
 * the message is all the supplier gets. `getErrorMessage` stops at the
 * envelope's `message`, which for a field error is the useless "Validation
 * failed" — the sentence that names the problem sits under `details.fields`
 * (an empty file, a file over the size cap).
 */
function uploadErrorMessage(err: unknown): string {
  const data = (err as { response?: { data?: Record<string, unknown> } })?.response?.data;
  const fields = (data?.['details'] as { fields?: Record<string, string[] | string> } | undefined)?.fields;
  if (fields) {
    const said = Object.values(fields)
      .flatMap((value) => (Array.isArray(value) ? value : [value]))
      .filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
    if (said.length > 0) return said.join(' ');
  }
  return getErrorMessage(err, 'The file could not be uploaded.');
}

/** Newest period first; Supplier Performance before Quality inside a period. */
function groupByPeriod(rows: SupplierUploadTracking[]): ObligationPeriod[] {
  const byKey = new Map<string, ObligationPeriod>();
  for (const row of rows) {
    const key = periodKey(row);
    const existing = byKey.get(key);
    if (existing) {
      existing.rows.push(row);
    } else {
      byKey.set(key, { key, label: periodLabelFor(row), dueDate: row.due_date, rows: [row] });
    }
  }
  const order = (row: SupplierUploadTracking) => DATASET_ORDER.indexOf(row.dataset_type);
  return Array.from(byKey.values())
    .sort((a, b) => b.key.localeCompare(a.key))
    .map((period) => ({ ...period, rows: [...period.rows].sort((a, b) => order(a) - order(b)) }));
}

/**
 * The supplier's own monthly obligations (last twelve months) and the upload
 * action that satisfies one of them.
 *
 * Calls: GET supplier-uploads/my_obligations/?periods=12 and supplier-uploads/periods/;
 * POST import-summaries/upload/ (file, template_id, period).
 *
 * @returns Grouped periods, load state, and the upload handler
 */
export function useMyObligations(): UseMyObligationsResult {
  const [uploadingId, setUploadingId] = useState<number | null>(null);
  const [failure, setFailure] = useState<UploadFailure | null>(null);

  // Last twelve months. Months the customer has not (yet) requested files for
  // still get a row per dataset so a supplier can send an earlier month; the
  // upload creates the obligation row on the way in.
  const query = useFetchData<SupplierUploadTracking[]>(async () => {
    const [{ data }, periodsRes] = await Promise.all([
      barrattAPI.getMyUploadObligations(12),
      barrattAPI.getUploadPeriods(),
    ]);
    const real = data.obligations ?? [];
    const template = real[0];
    const virtual: SupplierUploadTracking[] = [];
    for (const period of periodsRes.data.periods ?? []) {
      const [year, month] = period.period.split('-').map(Number);
      const start = `${period.period}-01`;
      const end = new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10);
      for (const dataset_type of DATASET_ORDER) {
        if (real.some((r) => periodKey(r) === period.period && r.dataset_type === dataset_type)) continue;
        virtual.push({
          id: -(year * 100 + month) * 10 - DATASET_ORDER.indexOf(dataset_type),
          customer: template?.customer ?? 0,
          customer_name: template?.customer_name,
          supplier: template?.supplier ?? 0,
          supplier_name: template?.supplier_name,
          period: period.period,
          period_label: period.label,
          period_start: start,
          period_end: end,
          dataset_type,
          dataset_type_display: datasetLabel({ dataset_type } as SupplierUploadTracking),
          due_date: period.due_date ?? end,
          status: 'pending',
          status_display: 'Not requested',
          records_count: 0,
          otif_pct: null,
          ncr_count: null,
          last_reminder_at: null,
          reminder_count: 0,
          created_at: start,
          updated_at: start,
        } as SupplierUploadTracking);
      }
    }
    return [...real, ...virtual];
  });

  // Stable: useFetchData wraps refetch in a no-deps useCallback.
  const { refetch } = query;
  const periods = useMemo(() => groupByPeriod(query.data ?? []), [query.data]);
  const customerName = query.data?.find((row) => row.customer_name)?.customer_name ?? null;

  const upload = useCallback(async (row: SupplierUploadTracking, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('template_id', String(TEMPLATE_ID_BY_DATASET[row.dataset_type]));
    formData.append('period', periodKey(row));

    const hadFileAlready = row.status === 'uploaded';
    setUploadingId(row.id);
    setFailure(null);
    try {
      const { data } = await barrattAPI.uploadFile(formData);

      // The endpoint answers 201 whether or not the file validated — a rejected
      // file arrives as is_success:false with its reasons in the exception rows,
      // so treating any 2xx as success reported "uploaded — 0 rows" in green and
      // left the month outstanding.
      if (data.is_success === false) {
        setFailure({
          summaryId: data.id,
          fileName: data.file_name || file.name,
          datasetType: row.dataset_type,
          datasetLabel: datasetLabel(row),
          periodLabel: periodLabelFor(row),
          problems: data.exceptions ?? [],
          hadFileAlready,
          totalRows: data.total_records,
          rejectedRows: data.rejected_rows,
          counts: data.exception_counts,
        });
        await refetch();
        return;
      }

      const rows = data.total_records ?? 0;
      const rowLabel = `${rows.toLocaleString()} row${rows === 1 ? '' : 's'}`;
      if (data.tracking) {
        toast.success(`${datasetLabel(row)} for ${periodLabelFor(row)} uploaded — ${rowLabel}`);
      } else {
        // Imported, but it satisfied no obligation — the month stays as it was,
        // and a green tick would say the opposite of what the page will show.
        toast.warning(
          `${datasetLabel(row)} read — ${rowLabel}, but ${periodLabelFor(row)} is not marked as received. ` +
            'Check the month on the file, or ask your customer whether they are collecting it.',
        );
      }
      await refetch();
    } catch (err: unknown) {
      console.error('Upload failed:', err);
      // A transport/permission failure has no exception rows to show; the
      // server's own message is the whole story, so it goes in the same modal.
      setFailure({
        summaryId: 0,
        fileName: file.name,
        datasetType: row.dataset_type,
        datasetLabel: datasetLabel(row),
        periodLabel: periodLabelFor(row),
        problems: [{
          id: 0, import_log: 0, row_number: 0, severity: 'error',
          message: uploadErrorMessage(err),
          raw_data: {}, created_at: new Date().toISOString(),
        }],
        hadFileAlready,
        transportError: true,
      });
    } finally {
      setUploadingId(null);
    }
  }, [refetch]);

  return {
    periods,
    customerName,
    loading: query.loading,
    error: query.error,
    retry: () => void refetch(),
    upload,
    uploadingId,
    failure,
    dismissFailure: () => setFailure(null),
  };
}
